import { errorText, forgetPushDevice } from "@nexxt/app-kit";
import { toast } from "@nexxt/ui";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { accountPaths } from "./access/sign-in";
import { accountClient } from "./client";

/**
 * The signed-in user with roles and permissions, and whether the session still owes a second factor (ADR-151,
 * ADR-157): until it adds one, it reaches only the page that adds it.
 */
export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: () => accountClient().getMe(),
  staleTime: 5 * 60_000,
});

export function useMe() {
  return useSuspenseQuery(meQuery).data;
}

/** Hides what the user cannot do; the gateway and the domain check again (ADR-014). */
export function useCan(permission: string): boolean {
  return useMe().permissions.includes(permission);
}

/**
 * The session ends on the gateway; a full load to the sign-in, so nothing of this user stays in memory. A refusal
 * leaves the user signed in, and says so.
 */
export async function signOut(): Promise<void> {
  await leave(() => accountClient().logout());
}

/** Every session of the user ends, this one too (ADR-147, point 8): then the same way out as `signOut`. */
export async function signOutEverywhere(): Promise<void> {
  await leave(() => accountClient().logoutEverywhere());
}

async function leave(close: () => Promise<unknown>): Promise<void> {
  const { unsubscribePush } = accountClient();
  // the next person in this browser gets none of this user's pushes (ADR-283)
  if (unsubscribePush) await forgetPushDevice({ unsubscribe: unsubscribePush });
  try {
    await close();
  } catch (error) {
    toast.error(errorText(error));
    return;
  }
  location.assign(accountPaths.signIn);
}
